import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { TeamMember } from '@/types/project';

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  assigned_to: string | null;
  due_date: string | null;
  status: 'pending' | 'in_progress' | 'completed';
  priority: number;
  created_at: string;
}

interface ProjectTasksProps {
  projectId: string;
}

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  pending: { label: 'Bekliyor', color: 'text-amber-700', bg: 'bg-amber-50', icon: 'ri-time-line' },
  in_progress: { label: 'Devam Ediyor', color: 'text-blue-700', bg: 'bg-blue-50', icon: 'ri-loader-4-line' },
  completed: { label: 'Tamamlandı', color: 'text-emerald-700', bg: 'bg-emerald-50', icon: 'ri-check-line' },
};

export default function ProjectTasks({ projectId }: ProjectTasksProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [projectTitle, setProjectTitle] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 0,
    status: 'pending' as Task['status']
  });
  
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'in_progress' | 'completed'>('all');

  // Fetch tasks from Supabase
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');

    const { data, error: fetchError } = await supabase
      .from('tasks')
      .select('*')
      .eq('project_id', projectId)
      .order('priority', { ascending: true });

    if (fetchError) {
      setError('Görevler yüklenirken bir hata oluştu');
      setLoading(false);
      return;
    }

    setTasks(data || []);
    setLoading(false);
  }, [projectId]);

  const fetchTeamMembers = useCallback(async () => {
    const { data } = await supabase
      .from('project_team')
      .select('*')
      .eq('project_id', projectId)
      .eq('category', 'takım')
      .order('full_name', { ascending: true });
    
    setTeamMembers(data || []);
  }, [projectId]);

  const fetchProjectTitle = useCallback(async () => {
    const { data } = await supabase
      .from('projects')
      .select('title')
      .eq('id', projectId)
      .single();
    if (data) setProjectTitle(data.title);
  }, [projectId]);

  useEffect(() => {
    fetchTasks();
    fetchTeamMembers();
    fetchProjectTitle();
  }, [fetchTasks, fetchTeamMembers, fetchProjectTitle]);

  // Set default priority when opening add form
  useEffect(() => {
    if (showAddTask && !editingTask) {
      const maxPriority = tasks.reduce((max, t) => Math.max(max, t.priority || 0), 0);
      setNewTask(prev => ({ ...prev, priority: maxPriority + 1, status: 'pending' }));
    }
  }, [showAddTask, tasks, editingTask]);

  const filteredTasks = statusFilter === 'all'
    ? tasks
    : tasks.filter((t) => t.status === statusFilter);

  const sendAssignmentEmail = async (task: any) => {
    if (!task.assigned_to) return;

    const assignedMember = teamMembers.find(m => m.full_name === task.assigned_to);
    if (!assignedMember || !assignedMember.email) {
      console.log('No email found for assigned member:', task.assigned_to);
      return;
    }

    setSendingEmail(true);
    try {
      const { error } = await supabase.functions.invoke('task-notification', {
        body: {
          taskTitle: task.title,
          taskDescription: task.description,
          projectTitle: projectTitle,
          recipientEmail: assignedMember.email,
          recipientName: assignedMember.full_name,
          dueDate: task.due_date
        }
      });
      
      if (error) throw error;
      alert('E-posta başarıyla gönderildi.');
    } catch (err) {
      console.error('Notification error:', err);
      alert('E-posta gönderilirken bir hata oluştu. Ayarlarınızı kontrol edin.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;

    setAdding(true);
    setError('');

    try {
      const targetPriority = newTask.priority || (tasks.length + 1);

      if (editingTask) {
        // UPDATE Logic
        // Handle priority shift if priority changed
        if (targetPriority !== editingTask.priority) {
            await reorderPriority(editingTask, targetPriority);
        }

        const { error: updateError } = await supabase
          .from('tasks')
          .update({
            title: newTask.title.trim(),
            description: newTask.description.trim() || null,
            assigned_to: newTask.assigned_to.trim() || null,
            due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null,
            status: newTask.status,
            priority: targetPriority
          })
          .eq('id', editingTask.id);

        if (updateError) throw updateError;
      } else {
        // INSERT Logic
        // 1. Shift existing tasks if necessary
        const { error: shiftError } = await supabase.rpc('shift_tasks_priority', {
            p_project_id: projectId,
            p_target_priority: targetPriority,
            p_increment: 1
        });

        if (shiftError) {
            // Manual fallback if RPC fails
            const { data: toShift } = await supabase
                .from('tasks')
                .select('id, priority')
                .eq('project_id', projectId)
                .gte('priority', targetPriority);
            
            if (toShift && toShift.length > 0) {
                for (const item of toShift) {
                    await supabase.from('tasks').update({ priority: item.priority + 1 }).eq('id', item.id);
                }
            }
        }

        // 2. Insert new task
        const { error: insertError } = await supabase
          .from('tasks')
          .insert({
            project_id: projectId,
            title: newTask.title.trim(),
            description: newTask.description.trim() || null,
            assigned_to: newTask.assigned_to.trim() || null,
            due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null,
            status: 'pending',
            priority: targetPriority,
          });

        if (insertError) throw insertError;
      }

      await fetchTasks();
      closeForm();
    } catch {
      setError('Görev kaydedilirken bir hata oluştu');
    } finally {
      setAdding(false);
    }
  };

  const reorderPriority = async (task: Task, newPriority: number) => {
    if (newPriority === task.priority) return;
    if (newPriority < 1) newPriority = 1;

    // Logic:
    // If newP < oldP: Shift [newP, oldP-1] up by 1
    // If newP > oldP: Shift [oldP+1, newP] down by 1
    
    if (newPriority < task.priority) {
      const { data: toShift } = await supabase
        .from('tasks')
        .select('id, priority')
        .eq('project_id', projectId)
        .gte('priority', newPriority)
        .lt('priority', task.priority);
      
      if (toShift) {
        for (const item of toShift) {
          await supabase.from('tasks').update({ priority: item.priority + 1 }).eq('id', item.id);
        }
      }
    } else {
      const { data: toShift } = await supabase
        .from('tasks')
        .select('id, priority')
        .eq('project_id', projectId)
        .gt('priority', task.priority)
        .lte('priority', newPriority);
      
      if (toShift) {
        for (const item of toShift) {
          await supabase.from('tasks').update({ priority: item.priority - 1 }).eq('id', item.id);
        }
      }
    }
  };

  const updatePriority = async (task: Task, newPriority: number) => {
    setLoading(true);
    try {
      await reorderPriority(task, newPriority);
      await supabase.from('tasks').update({ priority: newPriority }).eq('id', task.id);
      await fetchTasks();
    } catch {
      setError('Sıralama güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (taskId: string, newStatus: string) => {
    const { error: updateError } = await supabase
      .from('tasks')
      .update({ status: newStatus })
      .eq('id', taskId);

    if (updateError) {
      setError('Durum güncellenirken bir hata oluştu');
      return;
    }

    setTasks(tasks.map((t) =>
      t.id === taskId ? { ...t, status: newStatus as Task['status'] } : t
    ));
  };

  const deleteTask = async (taskId: string) => {
    if (!confirm('Bu görevi silmek istediğinize emin misiniz?')) return;

    const { error: deleteError } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (deleteError) {
      setError('Görev silinirken bir hata oluştu');
      return;
    }

    setTasks(tasks.filter((t) => t.id !== taskId));
    if (editingTask?.id === taskId) closeForm();
  };

  const openEdit = (task: Task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      due_date: task.due_date ? task.due_date.slice(0, 16) : '',
      priority: task.priority,
      status: task.status
    });
    setShowAddTask(true);
  };

  const closeForm = () => {
    setShowAddTask(false);
    setEditingTask(null);
    setNewTask({ title: '', description: '', assigned_to: '', due_date: '', priority: 0, status: 'pending' });
  };

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Görevler</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            {tasks.filter((t) => t.status === 'completed').length}/{tasks.length} tamamlandı
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
          >
            <option value="all">Tümü</option>
            <option value="pending">Bekliyor</option>
            <option value="in_progress">Devam Ediyor</option>
            <option value="completed">Tamamlandı</option>
          </select>
          <button
            onClick={() => setShowAddTask(true)}
            className="px-3 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Görev Ekle
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Add/Edit Task Form */}
      {showAddTask && (
        <form onSubmit={handleAddTask} className="bg-white rounded-xl p-5 border border-stone-200 space-y-4 shadow-md ring-2 ring-amber-500/10">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-stone-800">{editingTask ? 'Görevi Düzenle' : 'Yeni Görev'}</h3>
            {editingTask && (
              <button 
                type="button" 
                onClick={() => deleteTask(editingTask.id)}
                className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
              >
                <i className="ri-delete-bin-line"></i>
                Bu Görevi Sil
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-sm font-medium text-stone-700 mb-1">Görev Adı</label>
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                required
                disabled={adding}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
                placeholder="Görev başlığı..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Sıra No</label>
              <input
                type="number"
                min="1"
                value={newTask.priority}
                onChange={(e) => setNewTask({ ...newTask, priority: parseInt(e.target.value) || 1 })}
                required
                disabled={adding}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Atanan Kişi</label>
              <select
                value={newTask.assigned_to || ''}
                onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                disabled={adding}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 cursor-pointer"
              >
                <option value="">Atanmamış</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.full_name}>
                    {member.full_name} {member.role ? `(${member.role})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Durum</label>
              <select
                value={newTask.status}
                onChange={(e) => setNewTask({ ...newTask, status: e.target.value as Task['status'] })}
                disabled={adding}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer disabled:opacity-50"
              >
                <option value="pending">Bekliyor</option>
                <option value="in_progress">Devam Ediyor</option>
                <option value="completed">Tamamlandı</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Bitiş Tarihi</label>
            <input
              type="datetime-local"
              value={newTask.due_date}
              onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
              disabled={adding}
              className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Açıklama</label>
            <textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              rows={3}
              disabled={adding}
              className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none disabled:opacity-50"
              placeholder="Görev açıklaması..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={closeForm}
              disabled={adding}
              className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              İptal
            </button>
            {newTask.assigned_to && (
              <button
                type="button"
                onClick={() => sendAssignmentEmail(newTask)}
                disabled={adding || sendingEmail}
                className="flex-1 py-2.5 bg-stone-100 text-stone-700 border border-stone-200 rounded-lg text-sm font-medium hover:bg-stone-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {sendingEmail ? (
                  <div className="w-4 h-4 border-2 border-stone-400 border-t-stone-700 rounded-full animate-spin" />
                ) : (
                  <i className="ri-mail-send-line"></i>
                )}
                E-posta Gönder
              </button>
            )}
            <button
              type="submit"
              disabled={adding}
              className="flex-1 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {adding ? 'Kaydediliyor...' : (editingTask ? 'Güncelle' : 'Ekle')}
            </button>
          </div>
        </form>
      )}

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-stone-400 text-sm">Henüz görev bulunmuyor</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => {
            const status = statusConfig[task.status];
            const overdue = isOverdue(task.due_date);

            return (
              <div
                key={task.id}
                onClick={() => openEdit(task)}
                className="bg-white rounded-xl p-4 border border-stone-200 hover:border-amber-400/50 hover:shadow-md transition-all shadow-sm cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  {/* Priority Number */}
                  <div className="flex flex-col items-center gap-1 mt-1 flex-shrink-0">
                    <span className="text-xs font-bold text-stone-400 bg-stone-50 px-2 py-1 rounded">#{task.priority}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-semibold text-sm ${task.status === 'completed' ? 'text-stone-400 line-through' : 'text-stone-800'}`}>
                        {task.title}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.bg} ${status.color}`}>
                        <i className={status.icon}></i>
                        {status.label}
                      </span>
                    </div>

                    {task.description && (
                      <p className="text-sm text-stone-500 mt-1 line-clamp-1 group-hover:line-clamp-none transition-all">{task.description}</p>
                    )}

                    <div className="flex items-center gap-4 mt-3 text-xs text-stone-400">
                      {task.assigned_to && (
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {task.assigned_to}
                        </span>
                      )}
                      {task.due_date && (
                        <span className={`flex items-center gap-1 ${overdue ? 'text-red-500 font-medium' : ''}`}>
                          <i className="ri-calendar-line"></i>
                          {new Date(task.due_date).toLocaleDateString('tr-TR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        const newP = prompt('Yeni sıra numarasını girin:', task.priority.toString());
                        if (newP) updatePriority(task, parseInt(newP));
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
                      title="Sırayı Düzenle"
                    >
                      <i className="ri-sort-asc"></i>
                    </button>
                    {task.status !== 'completed' && (
                      <button
                        onClick={() => updateStatus(task.id, 'completed')}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-emerald-50 text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
                        title="Tamamlandı olarak işaretle"
                      >
                        <i className="ri-check-double-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
